/**
 * The Lanternfall channel — the hub's one shared chat.
 *
 * Polling, not a socket: the game socket only exists inside a raid, and the whole point of
 * this channel is the time *between* raids. `GET /api/chat?since=<id>` every few seconds is
 * cheap (an empty answer is a few dozen bytes) and it costs the server no connection state.
 *
 * The rhythm is deliberate. Three seconds while the room is talking, ten once two minutes
 * have passed with nothing said — a hub left open on a second monitor should not hammer a
 * free-tier VM all afternoon. The clock resets the moment anything happens: a new message
 * arrives, or the player sends one.
 *
 * Fail-soft like `net/api.ts`: if the endpoint is missing (this server predates the
 * channel) or unreachable, the feed goes 'quiet', the timer stops, and the dock says so
 * rather than retrying forever. Anything else — a 429, a 403 — is a line in the log and
 * polling carries on.
 */

import { ApiError, api } from './api.js';

export interface ChatMessage {
  id: number;
  /** Epoch milliseconds. */
  ts: number;
  playerId: number;
  name: string;
  /** 'player' | 'founder' | 'admin' | 'owner'. */
  role: string;
  text: string;
}

interface ChatFeedResponse {
  channel?: string;
  messages?: ChatMessage[];
  online?: number;
  latest?: number;
}

interface ChatPostResponse {
  message?: ChatMessage;
  online?: number;
}

/**
 * 'cold' — nothing read yet. 'live' — the channel answered. 'quiet' — the channel is not
 * there (missing endpoint, dead server); polling has stopped and only a reopen retries.
 */
export type ChatStatus = 'cold' | 'live' | 'quiet';

export interface ChatState {
  messages: ChatMessage[];
  online: number;
  status: ChatStatus;
  /** Last refusal worth showing the player ('slow down', 'banned'), or ''. */
  notice: string;
  sending: boolean;
}

/** Kept in memory; the server keeps the same number. */
const MAX_MESSAGES = 100;
const FAST_MS = 3000;
const SLOW_MS = 10_000;
/** Quiet for this long and the poll drops to `SLOW_MS`. */
const IDLE_MS = 120_000;
/** Mirrors the server's cap so the field can stop the player before the round trip. */
export const MAX_CHAT_LEN = 180;

type Listener = (s: ChatState) => void;

class ChatFeed {
  private state: ChatState = {
    messages: [],
    online: 0,
    status: 'cold',
    notice: '',
    sending: false,
  };
  private readonly listeners = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private token = '';
  private since = 0;
  /** When something last happened in the room; drives the fast/slow cadence. */
  private lastActivity = 0;
  private running = false;
  private polling = false;

  get current(): ChatState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private set(patch: Partial<ChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of [...this.listeners]) {
      try {
        fn(this.state);
      } catch (err) {
        console.error('[stillsun] chat listener threw', err);
      }
    }
  }

  /**
   * Called when the dock mounts (i.e. the hub became visible). A token change starts a
   * fresh conversation; the same token resumes where it left off, so reopening the hub
   * does not re-read a hundred lines.
   */
  start(token: string): void {
    if (token !== this.token) {
      this.token = token;
      this.since = 0;
      this.set({ messages: [], online: 0, status: 'cold', notice: '' });
    }
    if (this.running) return;
    this.running = true;
    // A reopen is worth one more try even after the channel went quiet — and the cursor
    // goes back to the top when it does, because everything said while we were not
    // listening sits between the old cursor and now. Re-reading at most 100 lines is
    // cheaper than showing a log with a hole in it; `merge` drops what we already hold.
    if (this.state.status === 'quiet') {
      this.since = 0;
      this.set({ status: 'cold' });
    }
    this.lastActivity = Date.now();
    void this.poll();
  }

  /** The hub hid, or the dock unmounted: stop talking to the server entirely. */
  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private schedule(): void {
    if (!this.running) return;
    if (this.timer !== null) clearTimeout(this.timer);
    const idle = Date.now() - this.lastActivity > IDLE_MS;
    this.timer = setTimeout(
      () => {
        this.timer = null;
        void this.poll();
      },
      idle ? SLOW_MS : FAST_MS,
    );
  }

  private async poll(): Promise<void> {
    if (!this.running || this.polling) return;
    if (this.token.length === 0) {
      // No session (offline boot): there is nobody to be, so say so and stop.
      this.set({ status: 'quiet' });
      this.stop();
      return;
    }
    this.polling = true;
    try {
      const res = await api<ChatFeedResponse>(`/api/chat?since=${String(this.since)}`, {
        token: this.token,
        timeoutMs: 6000,
      });
      // The channel lives in the server's memory, so a restart rewinds the ids to 1. When
      // the room's latest id is behind our cursor we are listening to a different room:
      // drop what we hold rather than merging old lines with new ones that share an id.
      if (typeof res.latest === 'number' && res.latest < this.since) {
        this.since = 0;
        this.set({ messages: [], status: 'live', notice: '' });
        this.schedule();
        return; // `finally` clears the in-flight flag on the way out.
      }
      const incoming = Array.isArray(res.messages) ? res.messages : [];
      if (incoming.length > 0) this.lastActivity = Date.now();
      this.set({
        messages: incoming.length > 0 ? merge(this.state.messages, incoming) : this.state.messages,
        online: typeof res.online === 'number' ? res.online : this.state.online,
        status: 'live',
        notice: '',
      });
      this.since = cursor(this.since, incoming);
    } catch (err) {
      if (err instanceof ApiError && (err.missing || err.status === 403)) {
        // Not deployed, unreachable, or we are not welcome: stop rather than spin.
        this.set({ status: 'quiet' });
        this.stop();
        return;
      }
      // A timeout or a 500 is weather, not a verdict — keep the log up and try again.
      console.warn('[stillsun] chat poll failed', err);
    } finally {
      this.polling = false;
    }
    this.schedule();
  }

  /** Sends a line. Resolves true when it landed, so the field can keep the text on failure. */
  async send(text: string): Promise<boolean> {
    const trimmed = text.trim().slice(0, MAX_CHAT_LEN);
    if (trimmed.length === 0 || this.state.sending) return false;
    if (this.token.length === 0) {
      this.set({ notice: 'no account — the channel cannot hear you' });
      return false;
    }
    this.set({ sending: true, notice: '' });
    try {
      const res = await api<ChatPostResponse>('/api/chat', {
        method: 'POST',
        body: { text: trimmed },
        token: this.token,
        timeoutMs: 6000,
      });
      this.lastActivity = Date.now();
      const message = res.message;
      if (message) {
        // `since` is deliberately NOT advanced to this id: anything said between our last
        // poll and this post sits in the gap, and skipping it would lose those lines
        // forever. The next poll re-reads our own message and `merge` drops the duplicate.
        this.set({
          messages: merge(this.state.messages, [message]),
          online: typeof res.online === 'number' ? res.online : this.state.online,
          status: 'live',
          sending: false,
        });
      } else {
        this.set({ sending: false });
      }
      // Somebody may have answered already; do not make them wait out the interval.
      this.schedule();
      return true;
    } catch (err) {
      const notice =
        err instanceof ApiError
          ? err.status === 429
            ? 'easy — one breath between lines'
            : err.status === 403
              ? 'the channel is closed to you'
              : err.missing
                ? 'the terminal is quiet'
                : err.message
          : 'the message did not send';
      if (err instanceof ApiError && err.missing) {
        this.set({ status: 'quiet' });
        this.stop();
      }
      this.set({ sending: false, notice });
      return false;
    }
  }

  clearNotice(): void {
    if (this.state.notice.length > 0) this.set({ notice: '' });
  }
}

/** Newest last, no duplicates, at most `MAX_MESSAGES`. */
function merge(held: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const known = new Set(held.map((m) => m.id));
  const fresh = incoming.filter((m) => !known.has(m.id));
  if (fresh.length === 0) return held;
  const all = [...held, ...fresh].sort((a, b) => a.id - b.id);
  return all.length > MAX_MESSAGES ? all.slice(-MAX_MESSAGES) : all;
}

function cursor(current: number, incoming: ChatMessage[]): number {
  let next = current;
  for (const m of incoming) if (m.id > next) next = m.id;
  return next;
}

export const chatFeed = new ChatFeed();
